import {
  Heading,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";

import { BundlesTable } from "./BundlesTable";
import actions from "../../actions";
import { KYDEventBundle } from "@common/interfaces/KYDEvent";
import "react-datepicker/dist/react-datepicker.css";
import { useDialog, useDialogWithOptions } from "../../Common/Dialog";
import { useLoading } from "../hooks/form";
import AddEditBundleDialog, {
  AddEditBundleDialogContext,
  withAddEditBundleDialog,
} from "./AddBundleDialog";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";

const Bundles = ({ currentUser }: { currentUser: KYDOrganizationUser }) => {
  const [bundles, setBundles] = useState<KYDEventBundle[] | null>(null);
  const [loading, updateLoading] = useLoading({
    init: false,
    event: false,
    bundle: false,
  });

  const dialog = useDialog();
  const navigate = useNavigate();

  const addBundleDialog = useDialogWithOptions(AddEditBundleDialogContext);

  const fetchBundles = useCallback(async () => {
    updateLoading("init", true);
    try {
      const _ = await actions.ADMIN.fetchEventBundles();
      setBundles(_);
    } catch (err) {
      dialog({ text: err.message });
    }
    updateLoading("init", false);
  }, [updateLoading, dialog]);

  useEffect(() => {
    fetchBundles();
  }, [updateLoading, dialog, fetchBundles]);

  const onCreateNewBundle = useCallback(
    async ({ title }: { title: string }) => {
      updateLoading("bundle", true);
      try {
        const bundle = await actions.ADMIN.createEventBundle({
          data: {
            title,
          },
        });
        addBundleDialog.close();
        navigate(`/bundles/${bundle.id}`);
      } catch (e) {
        dialog({
          text: e.message,
        });
      }
      updateLoading("bundle", false);
    },
    []
  );

  return (
    <Stack spacing={{ base: "8", lg: "6" }}>
      <AddEditBundleDialog
        isLoading={loading.bundle}
        onSave={onCreateNewBundle}
        onClose={() => {
          addBundleDialog.close();
        }}
      />
      <Stack
        direction={"row"}
        justify={"space-between"}
        w="full"
        alignItems={"center"}
        spacing="4"
        align={{ base: "start", lg: "center" }}
      >
        <Heading
          size={useBreakpointValue({ base: "xs", lg: "sm" })}
          fontWeight="medium"
        >
          Bundles
        </Heading>
        {currentUser?.scopes?.includes("organizations/write") ||
        currentUser?.scopes?.includes("organizations/bundles:write") ? (
          <HStack>
            <IconButton
              isDisabled={loading.event}
              variant={"outline"}
              aria-label="Add bundle"
              icon={<FaPlus />}
              onClick={() => {
                console.log(addBundleDialog);
                addBundleDialog.open();
              }}
            />
          </HStack>
        ) : null}
      </Stack>
      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading bundles...</Text>
        </VStack>
      )}
      {bundles && bundles.length > 0 && (
        <Stack overflowX={"scroll"} spacing={{ base: "5", lg: "6" }}>
          <BundlesTable bundles={bundles} />
        </Stack>
      )}
      {bundles && bundles.length === 0 && (
        <VStack>
          <Text>No bundles created yet</Text>
        </VStack>
      )}
    </Stack>
  );
};

export default withAddEditBundleDialog(Bundles);
